import type {
    ResearchEmail,
    ResearchFeedbackDetail,
    ResearchFeedbackResponse,
    ResearchFeedbackSentiment,
    ResearchRunDetail,
    ResearchRunsPage,
} from "models/ResearchFindings/ResearchFindings";
import BaseService from "./BaseService";

/** Client for the ResearchFindingsController (api/ResearchFindings). */
export class ResearchFindingsService extends BaseService {
    protected urlBase = "api/ResearchFindings";

    /** GET that tolerates an empty 200 body (serialized "no data"). */
    private async getJson<T>(
        url: string,
        fallback: T,
        params?: URLSearchParams,
    ): Promise<T> {
        const res = await this.get<Response>(
            params ?? new URLSearchParams(),
            url,
            undefined,
            undefined,
            true,
        );
        const text = await res.text();
        return text ? (JSON.parse(text) as T) : fallback;
    }

    /** Server-side paginated run list. */
    public getRuns(page: number, pageSize: number): Promise<ResearchRunsPage> {
        return this.getJson<ResearchRunsPage>(
            "runs",
            { items: [], totalCount: 0, page, pageSize },
            new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
            }),
        );
    }

    public getRun(runId: string): Promise<ResearchRunDetail | null> {
        return this.getJson<ResearchRunDetail | null>(`runs/${runId}`, null);
    }

    public getQuestionEmail(questionId: string): Promise<ResearchEmail | null> {
        return this.getJson<ResearchEmail | null>(
            `questions/${questionId}/email`,
            null,
        );
    }

    /**
     * Sends the question's report email to the given recipients. An empty list makes the
     * server send the report to the caller's own address (matches the KboFlow debug send).
     */
    public sendQuestionEmail(
        questionId: string,
        recipients: string[],
        reportId: string,
    ): Promise<{ sentTo: string[] }> {
        return this.post(
            JSON.stringify({ recipients, reportId }),
            new URLSearchParams(),
            `questions/${questionId}/send-email`,
        );
    }

    public submitQuestionFeedback(
        questionId: string,
        sentiment: ResearchFeedbackSentiment,
        text?: string,
    ): Promise<ResearchFeedbackResponse> {
        return this.post(
            JSON.stringify({ sentiment, text }),
            new URLSearchParams(),
            `questions/${questionId}/feedback`,
        );
    }

    /** All feedback for a question, newest first (the "view all feedback" list). */
    public getQuestionFeedback(
        questionId: string,
    ): Promise<ResearchFeedbackDetail[]> {
        return this.getJson<ResearchFeedbackDetail[]>(
            `questions/${questionId}/feedback`,
            [],
        );
    }

    /** Deletes a feedback entry (server enforces own-feedback only). */
    public deleteQuestionFeedback(
        questionId: string,
        feedbackId: string,
    ): Promise<ResearchFeedbackResponse> {
        return this.delete(
            new URLSearchParams(),
            `questions/${questionId}/feedback/${feedbackId}`,
        ).then((res) => res.json() as Promise<ResearchFeedbackResponse>);
    }
}

export default ResearchFindingsService;
